/**
 * RuneOrbit — two counter-rotating rings of runic glyphs around the eye.
 *
 * Outer ring rotates slow clockwise, inner ring fast counter-clockwise.
 * Glyphs glow red, with the occasional cyan one as a glitch accent.
 */
import React, { useMemo } from "react";

const RUNES = "ᚠᚢᚦᚨᚱᚲᚷᚹᚺᚾᛁᛃᛇᛈᛉᛊᛏᛒᛖᛗᛚᛜᛟᛞ";
const HEX = "0123456789ABCDEF";

function pickGlyphs(n, mix = 0.2) {
  const arr = [];
  for (let i = 0; i < n; i++) {
    if (Math.random() < mix) arr.push(HEX[Math.floor(Math.random() * HEX.length)]);
    else arr.push(RUNES[Math.floor(Math.random() * RUNES.length)]);
  }
  return arr;
}

function Ring({ radius, count, size, animation, mix, accent }) {
  const glyphs = useMemo(() => pickGlyphs(count, mix), [count, mix]);
  return (
    <div
      className={`absolute inset-0 ${animation}`}
      style={{ width: radius * 2, height: radius * 2 }}
      aria-hidden="true"
    >
      {glyphs.map((g, i) => {
        const angle = (i / glyphs.length) * 2 * Math.PI;
        const x = radius + Math.cos(angle) * radius;
        const y = radius + Math.sin(angle) * radius;
        const isAccent = Math.random() < 0.15;
        // Stable key derived from glyph content + slot index. The glyphs
        // array is computed once via useMemo (deps: count, mix) so slot
        // order never reshuffles between renders.
        return (
          <span
            key={`${g}-${i}`}
            className="absolute font-runic select-none"
            style={{
              left: x,
              top: y,
              transform: `translate(-50%, -50%) rotate(${
                (angle * 180) / Math.PI + 90
              }deg)`,
              fontSize: size,
              color: isAccent ? accent : "rgba(255, 26, 58, 0.75)",
              textShadow: isAccent
                ? "0 0 8px rgba(0, 224, 255, 0.65)"
                : "0 0 10px rgba(255, 26, 58, 0.7), 0 0 2px rgba(255,255,255,0.4)",
              fontWeight: 800,
            }}
          >
            {g}
          </span>
        );
      })}
    </div>
  );
}

export default function RuneOrbit({
  size = 640,
  className = "",
  accent = "rgba(0, 224, 255, 0.9)",
}) {
  return (
    <div
      className={`absolute pointer-events-none ${className}`}
      style={{
        width: size,
        height: size,
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
      }}
      data-testid="rune-orbit"
    >
      <Ring
        radius={size / 2}
        count={28}
        size={18}
        animation="rune-orbit-slow"
        mix={0.15}
        accent={accent}
      />
      <Ring
        radius={size / 2 - 60}
        count={20}
        size={14}
        animation="rune-orbit-reverse"
        mix={0.25}
        accent={accent}
      />
      <Ring
        radius={size / 2 - 110}
        count={12}
        size={11}
        animation="rune-orbit-fast"
        mix={0.30}
        accent={accent}
      />
    </div>
  );
}
